import React, { useState } from "react";
import { Card, Button, Table, Space, Typography, Modal, Tag, Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, PrinterOutlined, ArrowLeftOutlined, DeleteFilled } from "@ant-design/icons";
import {
  useFetchPrintQueueQuery,
  useRemoveFromPrintQueueMutation,
  useClearPrintQueueMutation,
  usePrintLabelsMutation,
} from "./label.service";

const { Title, Text } = Typography;
const { Column } = Table;

export default function PrintQueue() {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [printLoading, setPrintLoading] = useState(false);

  const { data: queueData, isLoading, refetch } = useFetchPrintQueueQuery();
  const [removeFromQueue] = useRemoveFromPrintQueueMutation();
  const [clearQueue] = useClearPrintQueueMutation();
  const [printLabels] = usePrintLabelsMutation();

  const queueItems = queueData?.data?.queue || [];

  const handleRemove = async (id) => {
    try {
      await removeFromQueue(id).unwrap();
      message.success("Item removed from queue");
      refetch();
    } catch (error) {
      message.error("Failed to remove item");
    }
  };

  const handleClearQueue = () => {
    Modal.confirm({
      title: "Clear Print Queue",
      content: "Are you sure you want to clear all items from the queue?",
      onOk: async () => {
        try {
          await clearQueue().unwrap();
          message.success("Queue cleared");
          refetch();
          setSelectedRowKeys([]);
        } catch (error) {
          message.error("Failed to clear queue");
        }
      },
    });
  };

  const handlePrint = async () => {
    const idsToPrint = selectedRowKeys.length > 0 ? selectedRowKeys : queueItems.map(item => item._id);
    
    if (idsToPrint.length === 0) {
      message.warning("No items to print");
      return;
    }

    setPrintLoading(true);
    try {
      await printLabels({ queueIds: idsToPrint }).unwrap();
      message.success(`Printing ${idsToPrint.length} label(s)`);
      setSelectedRowKeys([]);
      refetch();
    } catch (error) {
      message.error("Failed to print labels");
    } finally {
      setPrintLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/label/food-date-label")} />
          <Title level={4} style={{ margin: 0 }}>Print Queue</Title>
        </Space>
      }
      extra={
        <Space>
          <Button 
            danger 
            icon={<DeleteFilled />} 
            onClick={handleClearQueue}
            disabled={queueItems.length === 0}
          >
            Clear All
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printLoading}
            disabled={queueItems.length === 0}
          >
            Print {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : 'All'}
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <div style={{ marginBottom: 16, textAlign: "center" }}>
          <Tag color="blue" style={{ fontSize: "14px", padding: "4px 12px" }}>
            LABELS TO PRINT: {queueItems.length}
          </Tag>
        </div>

        <Table
          dataSource={queueItems}
          rowKey="_id"
          rowSelection={rowSelection}
          pagination={{ pageSize: 10 }}
        >
          <Column 
            title="Product" 
            dataIndex="productId"
            render={(product) => product?.name || "—"} 
          />
          <Column 
            title="Category" 
            dataIndex="productId"
            render={(product) => product?.categoryId?.name || "—"} 
          />
          <Column
            title="Quantity"
            dataIndex="quantity"
            render={(qty) => <Tag color="green">{qty}</Tag>}
          />
          <Column
            title="Production Date"
            dataIndex="productionDate"
            render={(date) => date ? new Date(date).toLocaleDateString() : "—"}
          />
          <Column
            title="Expiry Date"
            dataIndex="expiryDate"
            render={(date) => date ? new Date(date).toLocaleDateString() : "—"}
          />
          <Column
            title="Date Added"
            dataIndex="createdAt"
            render={(date) => new Date(date).toLocaleString()}
          />
          <Column
            title="Actions"
            align="center"
            render={(_, record) => (
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => handleRemove(record._id)}
              />
            )}
          />
        </Table>
      </Spin>
    </Card>
  );
}